import {ref, reactive, onMounted} from "vue";
import axios from "axios";

export function useDataManager({
   baseUrl,
   baseSearchUrl,
   defaultSortingField,
   defaultSortingFieldsOrder,
   filters: externalFilters = null,
   onResults = () => {},
   onError = () => {},
}) {
    const data = ref([]);
    const links = ref([]);
    const meta = ref([]);
    const loading = ref(false);
    const query = ref("");
    const searchLoaded = ref(false);
    const sortingField = ref(defaultSortingField);
    const sortingFieldsOrder = reactive({ ...defaultSortingFieldsOrder });
    const url = ref(null);
    const showArchived = ref(false);
    const filters = externalFilters || reactive({});

    const buildUrl = () => {
        const isSearchMode = query.value.length > 2;
        const base = isSearchMode ? baseSearchUrl : baseUrl;

        // Parse base URL to handle existing query parameters
        const parsedUrl = new URL(base, window.location.origin);
        const params = parsedUrl.searchParams;

        if (isSearchMode) {
            params.set('query', query.value);
        }

        if (showArchived.value) {
            params.set('archived', 'true');
        }

        params.set('sortingField', sortingField.value);
        params.set('sortingOrder', sortingFieldsOrder[sortingField.value]);

        return parsedUrl.pathname + '?' + params.toString();
    };

    // Adresse par défaut dès le départ : un filtre appliqué avant tout chargement (lien direct
    // vers une liste filtrée) partait sinon de « null » et interrogeait /null
    url.value = buildUrl();

    // Seule la dernière requête lancée remplit la liste : une réponse plus lente arrivée après est ignorée
    let lastRequestId = 0;

    const loadData = async (customUrl = null) => {
        const requestId = ++lastRequestId;
        loading.value = true;
        data.value = [];
        let fetchUrl = customUrl || buildUrl();
        fetchUrl = applyFilters(fetchUrl)
        url.value = fetchUrl;
        try {
            const response = await axios.get(fetchUrl);
            if (requestId !== lastRequestId) {
                return;
            }
            // Réponse inattendue (page HTML, erreur) : liste vide plutôt qu'un tableau qui plante
            data.value = response.data?.data ?? [];
            links.value = response.data?.links ?? [];
            meta.value = response.data?.meta ?? [];
            onResults(response.data);
        } catch (error) {
            if (requestId === lastRequestId) {
                onError(error);
            }
        } finally {
            if (requestId === lastRequestId) {
                loading.value = false;
            }
        }
    };

    function applyFilters(url) {
        const parsedUrl = new URL(url, window.location.origin);
        const params = parsedUrl.searchParams;

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                params.set(key, value); // 👈 remplace s’il existe, sinon ajoute
            } else {
                params.delete(key); // 👈 supprime si vide
            }
        });

        return parsedUrl.pathname + '?' + params.toString();
    }

    // Recherche passée dans l'URL (?q=…, « Voir tous les résultats » de la recherche globale) : elle
    // l'emporte sur la dernière recherche mémorisée par la liste, puis disparaît de l'adresse
    onMounted(() => {
        const params = new URLSearchParams(window.location.search);
        const urlQuery = (params.get('q') ?? '').trim();

        if (urlQuery.length <= 2) {
            return;
        }

        params.delete('q');
        const rest = params.toString();
        window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? '?' + rest : ''));

        query.value = urlQuery;
        searchLoaded.value = true;
        loadData();
    });

    const search = async () => {
        if (query.value.length > 2) {
            searchLoaded.value = true;
            await loadData();
        } else if (query.value.length === 0) {
            searchLoaded.value = false;
            await loadData();
        }
    };

    const clearSearch = async () => {
        query.value = "";
        searchLoaded.value = false;
        await loadData();
    };

    const changeSorting = async (field) => {
        sortingField.value = field;
        sortingFieldsOrder[field] =
            sortingFieldsOrder[field] === "asc" ? "desc" : "asc";
        if (searchLoaded.value || query.value.length > 2) {
            await search();
        } else {
            await loadData();
        }
    };

    const getArchived = async () => {
        showArchived.value = true;
        await loadData();
    };

    const hideArchived = async () => {
        showArchived.value = false;
        await loadData();
    };

    const updateFilters = (newFilters = {}) => {
        Object.entries(newFilters).forEach(([key, value]) => {
            filters[key] = value;
        });
    };

    return {
        data,
        links,
        meta,
        loading,
        query,
        searchLoaded,
        sortingField,
        sortingFieldsOrder,
        url,
        showArchived,
        loadData,
        search,
        clearSearch,
        changeSorting,
        getArchived,
        hideArchived,
        updateFilters,
    };
}
